'use client';

import { useEffect, Suspense } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import Script from 'next/script';

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

const GA_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;

const isValidGaId = (id?: string): boolean =>
  !!(id && id.startsWith('G-') && id !== 'your-google-analytics-id-here');

function GARouteTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!isValidGaId(GA_ID)) return;
    if (typeof window === 'undefined' || !window.gtag) return;

    const url = pathname + (searchParams.toString() ? `?${searchParams.toString()}` : '');
    window.gtag('event', 'page_view', {
      page_path: url,
      page_location: window.location.href,
    });
  }, [pathname, searchParams]);

  return null;
}

export default function GoogleAnalytics() {
  if (!isValidGaId(GA_ID)) {
    if (process.env.NODE_ENV === 'development') {
      console.info('[Analytics] ANALYTICS CONFIGURATION REQUIRED — Set NEXT_PUBLIC_GA_MEASUREMENT_ID in .env (must start with G-)');
    }
    return null;
  }

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`}
        strategy="afterInteractive"
      />
      <Script id="ga-init" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${GA_ID}', {
            page_path: window.location.pathname,
            send_page_view: true
          });
        `}
      </Script>
      <Suspense fallback={null}>
        <GARouteTracker />
      </Suspense>
    </>
  );
}

/** Track custom events — safe to call anywhere client-side */
export function trackEvent(eventName: string, params: Record<string, unknown> = {}): void {
  if (!isValidGaId(GA_ID)) return;
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', eventName, params);
}
